'use client';

import React, { useState } from 'react';
import { useMobileNavStore } from '@/store/mobileNavStore';
import Link from 'next/link';
import Image from 'next/image';
import IconCollection from './IconCollection/IconCollection';
import { getSocialPlatformIcon } from '@/helpers/socialIcons';
import { Branding } from '@/types/branding';
import type { ResolvedHeaderNavItem } from '@/helpers/headerNavigation.helper';
import classNames from 'classnames';

const MobileNavDrawer: React.FC<{
  navItems: ResolvedHeaderNavItem[];
  brandingData: Branding | null;
}> = ({ navItems, brandingData }) => {
  const { isDrawerOpen, closeDrawer } = useMobileNavStore();
  const [openDropdownKey, setOpenDropdownKey] = useState<string | null>(null);

  return (
    <>
      {isDrawerOpen && (
        <div className="default-nav-drawer-backdrop" onClick={closeDrawer} />
      )}
      <div
        className={`w-100 position-fixed top-0 nav-drawer-wrapper ${isDrawerOpen ? 'open' : ''}`}
        style={{
          left: isDrawerOpen ? 0 : '-100%',
          zIndex: '1050',
        }}
      >
        <div className={`default-nav-drawer top-0`}>
          <div className="">
            <div className="nav-drawer-branding-container mt-4 d-flex align-items-center ms-2">
              {brandingData?.logo ? (
                <Image
                  src={`${brandingData.logo.url || '/assets/logo.png'}`}
                  alt={brandingData.logo.alternativeText || brandingData.name}
                  width={40}
                  height={40}
                  className="me-3"
                />
              ) : (
                <IconCollection name="defaultLogo" />
              )}
              <div>
                <p className="h1 mb-0">{brandingData?.name || 'ASL Roma'}</p>
              </div>
            </div>
          </div>

          <div className="nav-drawer-menu-container">
            <div className="d-flex flex-column gap-4">
              {navItems.map((item) => {
                if (item.type === 'Dropdown') {
                  const isOpen = openDropdownKey === item.key;
                  return (
                    <div key={item.key} className="it-mobile-nav-accordion">
                      <button
                        type="button"
                        className="it-mobile-nav-accordion__toggle p-0 m-0 text-decoration-none teal-text fw-semibold fs-6 lh-base w-100 d-flex align-items-center justify-content-between bg-transparent border-0"
                        aria-expanded={isOpen}
                        onClick={() =>
                          setOpenDropdownKey(isOpen ? null : item.key)
                        }
                      >
                        <span>{item.label}</span>
                        <i
                          className={classNames(
                            'bi bi-chevron-down',
                            isOpen && 'it-mobile-nav-accordion__chevron--open',
                          )}
                          aria-hidden
                        />
                      </button>
                      {isOpen && item.children?.length ? (
                        <ul className="it-mobile-nav-accordion__list list-unstyled mb-0 mt-3 ps-3">
                          {item.children.map((child) => (
                            <li
                              key={`${child.href}-${child.label}`}
                              className="mb-3"
                            >
                              <Link
                                prefetch={false}
                                href={child.href}
                                className="p-0 m-0 text-decoration-none teal-text fw-normal fs-6 lh-base"
                                target={
                                  child.openInNewTab ? '_blank' : undefined
                                }
                                rel={
                                  child.openInNewTab
                                    ? 'noopener noreferrer'
                                    : undefined
                                }
                                onClick={closeDrawer}
                              >
                                {child.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  );
                }

                if (!item.href) return null;

                return (
                  <Link
                    prefetch={false}
                    key={item.key}
                    href={item.href}
                    className="p-0 m-0 text-decoration-none teal-text fw-semibold fs-6 lh-base"
                    target={item.openInNewTab ? '_blank' : undefined}
                    rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
                    onClick={closeDrawer}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="nav-drawer-social-container">
            <h2 className="mb-2 fs-6 lh-base fw-normal">Seguici su</h2>
            <div className="d-flex gap-3">
              {brandingData?.socialLinks?.map((social) => (
                <a
                  key={social.id}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-decoration-none"
                  aria-label={`Follow us on ${social.platform}`}
                >
                  {getSocialPlatformIcon(social.platform, 'brand')}
                </a>
              ))}
            </div>
          </div>
        </div>

        <button
          onClick={closeDrawer}
          className="closeBtn position-absolute top-0 mt-4 "
          aria-label="Close navigation"
        >
          <IconCollection name="modalCloseX" />
        </button>
      </div>
    </>
  );
};

export default MobileNavDrawer;
